'use client';

import React, { useState, useEffect } from 'react';
import Header from '@/components/common/Header';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';
import { createClient } from '@/lib/supabase/client';

interface Category {
  id: string;
  name: string;
  description: string | null;
  manufacturer_count: number;
  is_featured: boolean;
}

export default function IndustryCategoriesPage() {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('industry_categories')
      .select('*')
      .order('manufacturer_count', { ascending: false });

    if (error) {
      console.error('Error fetching categories:', error);
    } else if (data) {
      setCategories(data);
    }
    setLoading(false);
  };

  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (category.description && category.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = selectedFilter === 'all' ||
      (selectedFilter === 'trending' && category.is_featured);
    return matchesSearch && matchesFilter;
  });

  const totalManufacturers = categories.reduce((sum, cat) => sum + cat.manufacturer_count, 0);

  const stats = [
    { label: t('totalCategories'), value: categories.length.toString(), icon: 'Squares2X2Icon' },
    { label: t('manufacturers'), value: totalManufacturers.toString(), icon: 'BuildingOfficeIcon' },
    { label: t('trending'), value: categories.filter(c => c.is_featured).length.toString(), icon: 'FireIcon' }
  ];

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-background pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading categories...</p>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-card to-background border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-12">
            <div className="text-center mb-10">
              <h1 className="text-4xl md:text-5xl font-headline font-bold text-foreground mb-4">
                {t('industryCategories')}
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {t('categoriesDesc')}
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              {stats.map((stat, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-5 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mb-3 mx-auto">
                    <Icon name={stat.icon as any} size={24} variant="outline" className="text-primary" />
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-foreground mb-1">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Search and Filter */}
            <div className="max-w-2xl mx-auto space-y-4">
              <div className="relative">
                <Icon name="MagnifyingGlassIcon" size={20} variant="outline" className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-card border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground placeholder:text-muted-foreground"
                />
              </div>

              <div className="flex justify-center space-x-2">
                <button
                  onClick={() => setSelectedFilter('all')}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${selectedFilter === 'all'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-card text-muted-foreground border border-border hover:border-primary/50'
                    }`}
                >
                  All Categories
                </button>
                <button
                  onClick={() => setSelectedFilter('trending')}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center space-x-2 ${selectedFilter === 'trending'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-card text-muted-foreground border border-border hover:border-primary/50'
                    }`}
                >
                  <Icon name="FireIcon" size={16} variant="solid" />
                  <span>Featured</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Categories Grid */}
        <section className="max-w-7xl mx-auto px-6 py-10">
          <div className="mb-6">
            <p className="text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{filteredCategories.length}</span> {filteredCategories.length === 1 ? 'category' : 'categories'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCategories.map((category) => (
              <article key={category.id} className="bg-card border border-border rounded-2xl p-6 hover:shadow-lg transition-all duration-200 hover:border-primary/50 group">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-14 h-14 bg-muted rounded-xl flex items-center justify-center text-3xl group-hover:scale-110 transition-transform duration-200">
                      🏭
                    </div>
                    <div>
                      <h3 className="text-lg font-headline font-semibold text-foreground group-hover:text-primary transition-colors mb-1">
                        {category.name}
                      </h3>
                      {category.is_featured && (
                        <span className="inline-flex items-center space-x-1 text-xs font-semibold text-warning">
                          <Icon name="FireIcon" size={12} variant="solid" />
                          <span>Featured</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {category.description || 'Explore manufacturers in this category'}
                </p>

                <div className="mb-4 py-3 border-y border-border">
                  <div className="text-xs text-muted-foreground mb-0.5">Manufacturers</div>
                  <div className="text-lg font-bold text-foreground">{category.manufacturer_count}</div>
                </div>

                <a
                  href={`/manufacturer-directory?category=${encodeURIComponent(category.name)}`}
                  className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <span>Explore Category</span>
                  <Icon name="ArrowRightIcon" size={16} variant="outline" />
                </a>
              </article>
            ))}
          </div>

          {filteredCategories.length === 0 && (
            <div className="text-center py-16">
              <Icon name="Squares2X2Icon" size={48} variant="outline" className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No categories found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filter</p>
            </div>
          )}
        </section>
      </main>
    </>
  );
}
